// A run's timeline, newest first: what happened (status changes, POs sent and moved, the CM's output and scrap,
// receipts at HHL, the opening balance), written by the server as it happens with who and when, and the notes, photos,
// QC reports, golden-sample notes and messages people add, each with files. An entry can carry a past date (when it
// really happened); when that's far from when it was recorded, both show.
import { Camera, CircleDot, ClipboardCheck, Factory, FileText, MessageSquare, PackageCheck, Send, Sparkles, StickyNote, Trash2, Truck } from "lucide-react";
import { useMemo, useState } from "react";
import type { ProductionEvent } from "../../../../../shared/db.gen.ts";
import { EVENT_KIND_LABELS, type EventKind, isManualEvent, MANUAL_EVENT_KINDS, type ManualEventKind } from "../../../../../shared/runs.ts";
import { shortDate } from "../../../lib/costs.ts";
import { can, mutate } from "../../../lib/mutate.ts";
import { useRows } from "../../../lib/sync.ts";
import { Attachments } from "../../../ui/Attachments.tsx";
import { Badge, Button, cx, Input, Select, Textarea } from "../../../ui/controls.tsx";
import { PaneLink } from "../../../ui/SidePane.tsx";
import { Card } from "../Workspace.tsx";
import type { RunProps } from "./RunsTab.tsx";

const ICON: Record<EventKind, typeof StickyNote> = {
  status: CircleDot,
  po_linked: FileText,
  po_unlinked: FileText,
  po_sent: Send,
  po_status: Truck,
  po_received: PackageCheck,
  cm_output: Factory,
  cm_scrap: Factory,
  opening: Sparkles,
  note: StickyNote,
  photo: Camera,
  qc_report: ClipboardCheck,
  golden_sample: Sparkles,
  message: MessageSquare,
};
const when = new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
const localInput = (d: Date) => new Date(d.getTime() - d.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);

export function RunTimeline({ run }: RunProps) {
  const all = useRows("production_event");
  const users = useRows("app_user");
  const events = useMemo(
    () => all.filter((e) => e.run_id === run.id).sort((a, b) => b.at.localeCompare(a.at) || b.created_at.localeCompare(a.created_at)),
    [all, run.id],
  );
  const [filter, setFilter] = useState<"all" | "people" | "auto">("all");
  const shown = events.filter((e) => filter === "all" || (filter === "people") === isManualEvent(e.kind));
  const name = (id: string | null) => {
    const u = users.find((x) => x.id === id);
    return u ? u.name || u.email : "HHL Ops";
  };
  return (
    <div className="grid grid-cols-[minmax(0,1fr)_380px] items-start gap-3">
      <Card
        title="Timeline"
        note="Newest first"
        actions={
          <div className="flex rounded-md border border-line p-0.5">
            {(
              [
                ["all", `All ${events.length}`],
                ["people", "Notes & files"],
                ["auto", "What happened"],
              ] as const
            ).map(([v, label]) => (
              <button
                key={v}
                type="button"
                onClick={() => setFilter(v)}
                className={cx("h-6 rounded px-2 text-[12px]", filter === v ? "bg-selected font-medium" : "text-muted hover:bg-hover")}
              >
                {label}
              </button>
            ))}
          </div>
        }
      >
        <ol>
          {shown.map((e) => (
            <Entry key={e.id} e={e} by={name(e.by)} />
          ))}
          {!shown.length && <li className="px-4 py-4 text-muted">Nothing yet.</li>}
        </ol>
      </Card>
      {can("production_event.create") && <Composer runId={run.id} />}
    </div>
  );
}

function Entry({ e, by }: { e: ProductionEvent; by: string }) {
  const Icon = ICON[e.kind] ?? StickyNote;
  const manual = isManualEvent(e.kind);
  const late = Math.abs(new Date(e.created_at).getTime() - new Date(e.at).getTime()) > 36 * 3_600_000;
  const data = (e.data ?? {}) as { po_id?: string };
  return (
    <li className="flex gap-3 border-t border-line px-4 py-2.5 first:border-t-0">
      <span
        className={cx(
          "mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-full",
          manual ? "bg-accent-soft text-accent-text" : e.kind === "opening" ? "bg-warn-soft text-warn" : "bg-surface-2 text-muted",
        )}
      >
        <Icon className="size-3.5" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          <span className="min-w-0 font-medium">{e.title || EVENT_KIND_LABELS[e.kind]}</span>
          {manual && <Badge tone="accent">{EVENT_KIND_LABELS[e.kind]}</Badge>}
          {data.po_id && (
            <PaneLink kind="po" id={data.po_id} to={`/pos?open=${data.po_id}`} className="text-[11px] text-accent-text hover:underline">
              Open PO
            </PaneLink>
          )}
        </div>
        {e.body && <div className="mt-0.5 text-[12px] whitespace-pre-wrap text-muted">{e.body}</div>}
        <div className="mt-0.5 text-[11px] text-faint">
          {when.format(new Date(e.at))} · {by}
          {late && ` · recorded ${shortDate(e.created_at)}`}
        </div>
        {manual ? (
          <div className="mt-1.5">
            <Attachments
              owner={{ type: "production_event", id: e.id }}
              hint="Drop photos, the QC report or the golden-sample sign-off here, or paste a screenshot"
            />
          </div>
        ) : (
          <div className="mt-1">
            <Attachments owner={{ type: "production_event", id: e.id }} compact />
          </div>
        )}
      </div>
      {manual && can("production_event.remove") && (
        <button
          type="button"
          onClick={() => confirm("Delete this entry and its files?") && mutate("production_event.remove", { id: e.id })}
          className="h-fit rounded p-1 text-faint hover:bg-danger-soft hover:text-danger"
          title="Delete"
        >
          <Trash2 className="size-3.5" />
        </button>
      )}
    </li>
  );
}

function Composer({ runId }: { runId: string }) {
  const [kind, setKind] = useState<ManualEventKind>("note");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [at, setAt] = useState(() => localInput(new Date()));
  const [last, setLast] = useState<string | null>(null);
  const add = async () => {
    if (!title.trim() && !body.trim()) return;
    const id = crypto.randomUUID();
    if (await mutate("production_event.create", { id, run_id: runId, kind, title: title.trim(), body: body.trim(), at: new Date(at).toISOString() })) {
      setLast(id);
      setTitle("");
      setBody("");
      setAt(localInput(new Date()));
    }
  };
  return (
    <Card title="Add to the timeline">
      <div className="flex flex-col gap-2 p-3">
        <div className="flex gap-2">
          <div className="w-40">
            <Select
              value={kind}
              options={MANUAL_EVENT_KINDS.map((k) => ({ value: k, label: EVENT_KIND_LABELS[k] }))}
              onChange={(e) => setKind(e.target.value as ManualEventKind)}
            />
          </div>
          <Input type="datetime-local" value={at} onChange={(e) => setAt(e.target.value)} title="When it happened (it can be in the past)" />
        </div>
        <Input
          value={title}
          placeholder={kind === "golden_sample" ? "Golden sample approved: black wireless" : "What happened"}
          onChange={(e) => setTitle(e.target.value)}
        />
        <Textarea value={body} placeholder="Details worth keeping (who said what, what to watch for)" onChange={(e) => setBody(e.target.value)} />
        <div className="flex items-center justify-between gap-2">
          <span className="text-[11px] text-faint">Files go on the entry once it's added.</span>
          <Button variant="primary" onClick={add} disabled={!title.trim() && !body.trim()}>
            Add
          </Button>
        </div>
        {last && (
          <div className="rounded-md border border-line p-2">
            <div className="mb-1 text-[12px] text-muted">Files for the entry just added</div>
            <Attachments owner={{ type: "production_event", id: last }} kind={kind === "photo" ? "photo" : kind === "qc_report" ? "report" : undefined} />
          </div>
        )}
      </div>
    </Card>
  );
}
